import type { CashSessionStatus, Role } from './constants'
import type {
  AppSettings,
  CatalogSnapshot,
  Category,
  CategoryAttribute,
  CheckoutInput,
  CostLogEntry,
  CreateInvoiceInput,
  CreateInvoiceItemInput,
  CreateSubscriptionInput,
  Customer,
  Employee,
  ExpenseEntry,
  PayrollStatusRow,
  CreateProductInput,
  LoginUserSummary,
  MessageConversation,
  MessageThread,
  OnlineStoreStatus,
  OnlineOrderDetail,
  OnlineOrderStatus,
  OnlineOrderSummary,
  StoreBuyer,
  Product,
  ProductDetail,
  PriceOverrideLogEntry,
  PurchaseInvoiceDetail,
  PurchaseInvoiceSummary,
  RecordSubscriptionPaymentInput,
  ReportSummary,
  SaleDetail,
  SaleReview,
  SaleSummary,
  SaveCategoryAttributeInput,
  SaveCategoryInput,
  SaveCustomerInput,
  SaveImagesInput,
  SaveSubscriptionPlanInput,
  SaveVariantsInput,
  SetAttributeValuesInput,
  SetSubscriptionStatusInput,
  SetTagsInput,
  SessionUser,
  SetupStatus,
  LicenseStatus,
  StoreProfile,
  Subscription,
  WebMemberRow,
  WebSessionRow,
  SubscriptionPayment,
  SubscriptionPlan,
  SyncStatus,
  Tag,
  UserRow,
  Supplier,
  UpdateProductInput,
  UpdateSubscriptionInput,
  LocationStock,
  Location,
  CreateLocationInput,
  UpdateLocationInput,
  StoreDevice,
  TransferStatus,
  GlAccount,
  TrialBalance,
  ProfitAndLoss,
  BalanceSheet,
  JournalEntrySummary,
  JournalEntryDetail,
  ManualJournalInput,
  SaveGlAccountInput,
  TransferSummary,
  TransferDetail,
  CreateTransferInput,
  TransferLineInput,
  ReceiveTransferInput,
  CashSession,
  CashSessionDetail,
  CashExpected,
  OpenCashSessionInput,
  CashMovementInput,
  CloseCashSessionInput
} from './types'

/**
 * Coarse classes of data the UI can watch. A mutation (or an inbound sync) that
 * touches one of these makes the core push `data:changed` with the affected
 * topics, so any mounted screen refetches instead of showing stale rows.
 *
 * Intentionally coarse — one topic per screen's worth of data, not per table.
 * Over-notifying costs a refetch; under-notifying is a stale-UI bug.
 */
export const DATA_TOPICS = [
  'products',
  'categories',
  'tags',
  'suppliers',
  'purchases',
  'sales',
  'expenses',
  'customers',
  'subscriptions',
  'users',
  'settings',
  'license',
  'onlineOrders',
  'messages',
  'locations',
  'transfers',
  'cashSessions',
  'invoices',
  'gl'
] as const

/**
 * A runtime list as well as a type, because the web portal needs to enumerate every
 * topic: after an SSE reconnect it cannot know what it missed while disconnected, so
 * it invalidates all of them. Deriving the type from the array keeps the two from
 * drifting.
 */
export type DataTopic = (typeof DATA_TOPICS)[number]

/**
 * Single source of truth for every renderer↔main operation.
 * Channels are handled in src/main/ipc/ with session + role checks;
 * the renderer calls them through window.api.invoke.
 */
export interface IpcContract {
  'auth:listLoginUsers': { req: void; res: LoginUserSummary[] }
  'auth:login': { req: { userId: string; pin: string }; res: SessionUser }
  'auth:logout': { req: void; res: void }
  'auth:session': { req: void; res: SessionUser | null }
  'auth:setActingLocation': { req: { locationId: string }; res: SessionUser }
  'auth:verifySupervisorPin': {
    req: { pin: string }
    res: { approvedBy: string; approverName: string }
  }

  'products:list': {
    req: { search?: string; includeInactive?: boolean }
    res: Product[]
  }
  'products:create': { req: CreateProductInput; res: Product }
  'products:update': { req: UpdateProductInput; res: Product }
  'products:get': { req: { productId: string }; res: ProductDetail }
  /**
   * Everything the sell screen needs, in one call. `since` asks for only what has
   * changed; omitting it (or passing a cursor the server won't honour) returns a
   * full snapshot, which the response says so with `full`.
   */
  'inventory:stockByLocation': {
    req: { productId: string; variantId?: string | null }
    res: LocationStock[]
  }
  'catalog:sync': { req: { since?: string }; res: CatalogSnapshot }
  'products:updateCostPrice': {
    req: { productId: string; variantId?: string | null; newCostCents: number }
    res: Product
  }
  'products:adjustStock': {
    req: {
      productId: string
      variantId?: string | null
      deltaQty: number
      reason: 'manual_adjustment' | 'damaged'
      note?: string
    }
    res: Product
  }
  'products:costLogs': { req: { productId: string }; res: CostLogEntry[] }
  'products:enableVariants': { req: { productId: string }; res: ProductDetail }
  'products:saveVariants': { req: SaveVariantsInput; res: ProductDetail }
  'products:saveImages': { req: SaveImagesInput; res: ProductDetail }
  'products:setAttributeValues': { req: SetAttributeValuesInput; res: ProductDetail }
  'products:setTags': { req: SetTagsInput; res: Tag[] }

  'categories:list': { req: { includeInactive?: boolean }; res: Category[] }
  'categories:save': { req: SaveCategoryInput; res: Category }
  'categories:listAttributes': {
    req: { categoryId: string; includeInactive?: boolean }
    res: CategoryAttribute[]
  }
  'categories:saveAttributes': {
    req: { categoryId: string; attributes: SaveCategoryAttributeInput[] }
    res: CategoryAttribute[]
  }
  'tags:list': { req: void; res: Tag[] }

  'suppliers:list': { req: { includeInactive?: boolean }; res: Supplier[] }
  'suppliers:create': {
    req: { name: string; phone?: string; note?: string }
    res: Supplier
  }
  'purchases:list': { req: void; res: PurchaseInvoiceSummary[] }
  'purchases:get': { req: { invoiceId: string }; res: PurchaseInvoiceDetail }
  'purchases:create': { req: CreateInvoiceInput; res: PurchaseInvoiceDetail }
  'purchases:receive': { req: { invoiceId: string }; res: PurchaseInvoiceDetail }
  'purchases:logPayment': {
    req: { invoiceId: string; amountCents: number; method: 'cash' | 'card' | 'transfer'; note?: string; cashSessionId?: string | null }
    res: PurchaseInvoiceDetail
  }
  'purchases:addItems': {
    req: {
      invoiceId: string
      items: CreateInvoiceItemInput[]
    }
    res: PurchaseInvoiceDetail
  }
  'purchases:setItemVariant': {
    req: { invoiceId: string; itemId: string; variantId: string | null }
    res: PurchaseInvoiceDetail
  }
  'purchases:cancel': { req: { invoiceId: string }; res: PurchaseInvoiceDetail }

  'sales:checkout': { req: CheckoutInput; res: SaleDetail }
  /**
   * Claim a block of receipt numbers up front, so a till can print a real receipt
   * the instant the cashier hits Charge instead of waiting on a round trip to find
   * out what to number it. Blocks are handed out under the same advisory lock that
   * guarded the counter, so two browsers can never overlap.
   */
  'sales:reserveReceipts': {
    req: { count: number }
    res: { terminal: string; from: number; to: number; numbers: string[] }
  }
  /** Sales the cloud accepted but could not fully honour. See SaleReview. */
  'sales:reviews': { req: { resolved?: boolean; limit?: number }; res: SaleReview[] }
  'sales:openReviewCount': { req: void; res: number }
  'sales:resolveReview': { req: { reviewId: string }; res: SaleReview }
  'sales:list': {
    req: { from?: string; to?: string; limit?: number; locationId?: string | null }
    res: SaleSummary[]
  }
  'sales:get': { req: { transactionId: string }; res: SaleDetail }
  'returns:process': {
    req: {
      saleItemId: string
      quantity: number
      refundAmountCents: number
      restock: boolean
      reason?: string
      cashSessionId?: string | null
    }
    res: SaleDetail
  }

  'employees:list': { req: { includeInactive?: boolean }; res: Employee[] }
  'employees:create': {
    req: { fullName: string; monthlySalaryCents: number }
    res: Employee
  }
  'employees:update': {
    req: {
      employeeId: string
      fullName?: string
      monthlySalaryCents?: number
      isActive?: boolean
    }
    res: Employee
  }
  'expenses:list': { req: { from?: string; to?: string }; res: ExpenseEntry[] }
  'expenses:create': {
    req: {
      category: 'utilities' | 'rent' | 'payroll' | 'damaged_goods' | 'other'
      amountCents: number
      description?: string
      expenseDate: string
      paymentMethod?: 'cash' | 'card' | 'transfer'
      cashSessionId?: string | null
    }
    res: ExpenseEntry
  }
  'payroll:status': { req: { year: number; month: number }; res: PayrollStatusRow[] }
  'payroll:pay': {
    req: { employeeId: string; year: number; month: number }
    res: PayrollStatusRow[]
  }

  'reports:summary': {
    req: { from: string; to: string; locationId?: string | null }
    res: ReportSummary
  }
  'reports:priceOverrides': {
    req: { from: string; to: string; locationId?: string | null }
    res: PriceOverrideLogEntry[]
  }

  'locations:list': { req: void; res: Location[] }
  'locations:create': { req: CreateLocationInput; res: Location }
  'locations:update': { req: UpdateLocationInput; res: Location }
  'devices:list': { req: void; res: StoreDevice[] }

  'transfers:list': {
    req: { direction?: 'incoming' | 'outgoing'; status?: TransferStatus; locationId?: string | null }
    res: TransferSummary[]
  }
  'transfers:get': { req: { transferId: string }; res: TransferDetail }
  'transfers:create': { req: CreateTransferInput; res: TransferDetail }
  'transfers:updateLines': { req: { transferId: string; lines: TransferLineInput[] }; res: TransferDetail }
  'transfers:dispatch': { req: { transferId: string }; res: TransferDetail }
  'transfers:receive': { req: ReceiveTransferInput; res: TransferDetail }
  'transfers:cancel': { req: { transferId: string }; res: TransferDetail }
  'transfers:pendingCount': { req: void; res: number }

  'cash:current': { req: void; res: CashSession | null }
  'cash:open': { req: OpenCashSessionInput; res: CashSessionDetail }
  'cash:move': { req: CashMovementInput; res: CashSessionDetail }
  'cash:expected': { req: { sessionId: string }; res: CashExpected }
  'cash:close': { req: CloseCashSessionInput; res: CashSessionDetail }
  'cash:list': { req: { status?: CashSessionStatus; locationId?: string | null; limit?: number }; res: CashSession[] }
  'cash:get': { req: { sessionId: string }; res: CashSessionDetail }

  'gl:accounts': { req: { includeInactive?: boolean }; res: GlAccount[] }
  'gl:saveAccount': { req: SaveGlAccountInput; res: GlAccount }
  'gl:setAccountActive': { req: { accountId: string; active: boolean }; res: GlAccount }
  'gl:postManual': { req: ManualJournalInput; res: JournalEntryDetail }
  'gl:trialBalance': { req: { from: string; to: string; locationId?: string | null }; res: TrialBalance }
  'gl:profitAndLoss': { req: { from: string; to: string; locationId?: string | null }; res: ProfitAndLoss }
  'gl:balanceSheet': { req: { asAt: string; locationId?: string | null }; res: BalanceSheet }
  'gl:ledger': { req: { from: string; to: string; locationId?: string | null; accountId?: string | null; limit?: number }; res: JournalEntrySummary[] }
  'gl:entry': { req: { entryId: string }; res: JournalEntryDetail }
  'gl:rebuild': { req: void; res: { posted: number; scanned: number } }

  'customers:list': { req: { search?: string; includeInactive?: boolean }; res: Customer[] }
  'customers:get': { req: { customerId: string }; res: Customer }
  'customers:save': { req: SaveCustomerInput; res: Customer }
  'customers:setActive': { req: { customerId: string; isActive: boolean }; res: Customer }

  // Storefront enquiries. Rows arrive by SYNC — create_online_order() writes them cloud-side — so
  // nothing here ever creates one.
  'onlineOrders:list': {
    req: { status?: OnlineOrderStatus | 'open'; search?: string; limit?: number }
    res: OnlineOrderSummary[]
  }
  'onlineOrders:get': { req: { orderId: string }; res: OnlineOrderDetail }
  'onlineOrders:setStatus': {
    req: { orderId: string; status: OnlineOrderStatus; sellerNote?: string | null }
    res: OnlineOrderDetail
  }
  /** Badge count for the nav. One COUNT, deliberately not folded into :list. */
  'storeBuyers:list': {
    req: { search?: string; limit?: number }
    res: StoreBuyer[]
  }
  'storeBuyers:update': {
    req: { buyerRowId: string; sellerNote?: string | null; isBlocked?: boolean }
    res: StoreBuyer
  }
  'onlineOrders:newCount': { req: void; res: { newCount: number } }

  // Storefront messaging. Conversations arrive by SYNC — only send_buyer_message() creates one — so
  // there is no `:create`. A reply is the one thing this surface writes locally, which is what makes
  // answering a shopper work on a till with no connection.
  'messages:list': {
    req: { filter?: 'unread' | 'all'; search?: string; limit?: number }
    res: MessageConversation[]
  }
  'messages:thread': { req: { conversationId: string }; res: MessageThread }
  'messages:send': { req: { conversationId: string; body: string }; res: MessageThread }
  /** Moves the store-wide read watermark. Store-wide because there is no per-user unread flag. */
  'messages:markRead': { req: { conversationId: string }; res: MessageThread }
  /** Badge count for the nav: unread THREADS, not unread messages. One COUNT, not folded into :list. */
  'messages:unreadCount': { req: void; res: { unreadCount: number } }

  'subscriptionPlans:list': { req: { includeInactive?: boolean }; res: SubscriptionPlan[] }
  'subscriptionPlans:save': { req: SaveSubscriptionPlanInput; res: SubscriptionPlan }
  'subscriptionPlans:setActive': {
    req: { planId: string; isActive: boolean }
    res: SubscriptionPlan
  }

  'subscriptions:list': { req: { includeInactive?: boolean }; res: Subscription[] }
  'subscriptions:get': { req: { subscriptionId: string }; res: Subscription }
  'subscriptions:create': { req: CreateSubscriptionInput; res: Subscription }
  'subscriptions:update': { req: UpdateSubscriptionInput; res: Subscription }
  'subscriptions:setStatus': { req: SetSubscriptionStatusInput; res: Subscription }
  'subscriptions:recordPayment': { req: RecordSubscriptionPaymentInput; res: Subscription }
  'subscriptions:payments': { req: { subscriptionId: string }; res: SubscriptionPayment[] }

  'setup:status': { req: void; res: SetupStatus }
  'setup:createStore': {
    req: {
      supabaseUrl?: string
      anonKey?: string
      deviceEmail: string
      devicePassword: string
      terminalId: string
      storeName: string
      adminName: string
      adminPin: string
    }
    res: { storeId: string; joinCode: string }
  }
  'setup:joinStore': {
    req: {
      supabaseUrl?: string
      anonKey?: string
      deviceEmail: string
      devicePassword: string
      terminalId: string
      joinCode: string
      locationId?: string | null
    }
    res: { storeId: string; storeName: string }
  }
  'setup:suggestTerminalId': {
    req: { supabaseUrl?: string; anonKey?: string; joinCode: string }
    res: { terminalId: string }
  }
  'setup:listJoinLocations': {
    req: { supabaseUrl?: string; anonKey?: string; joinCode: string }
    res: { id: string; name: string; isPrimary: boolean }[]
  }
  /** Frees this till's device slot; the platform then destroys the database (window.api.resetTill). */
  'setup:resetTill': { req: { pin: string }; res: void }
  'license:status': { req: void; res: LicenseStatus }
  'license:activate': { req: { code: string }; res: LicenseStatus }

  'sync:status': { req: void; res: SyncStatus }
  'sync:now': { req: void; res: SyncStatus }

  'users:list': { req: void; res: UserRow[] }
  'users:create': {
    req: { fullName: string; role: Role; pin: string; canLoginToPos: boolean }
    res: UserRow
  }
  'users:update': {
    req: {
      userId: string
      fullName?: string
      role?: Role
      canLoginToPos?: boolean
      isActive?: boolean
    }
    res: UserRow
  }
  'users:resetPin': { req: { userId: string; newPin: string }; res: void }
  'settings:get': { req: void; res: AppSettings }
  'settings:update': {
    req: {
      currencySymbol?: string
      allowPriceOverrideWithoutApproval?: boolean
      subscriptionsEnabled?: boolean
      serialCaptureEnabled?: boolean
      requireOnlineSales?: boolean
      cashSessionRequired?: boolean
      vatRegistered?: boolean
      vatRateBp?: number
      pricesIncludeVat?: boolean
      turnoverThresholdCents?: number
      printFormat?: 'thermal' | 'a4'
    }
    res: AppSettings
  }
  // Web access. All READ_ONLY in the sync sense (see router.ts): they write
  // cloud-only tables, so there is nothing local to push.
  'webAccess:list': { req: void; res: WebMemberRow[] }
  'webAccess:invite': {
    req: { email: string; posUserId: string }
    // `link` is present only when the email failed — see InviteResult.
    res: { email: string; delivered: boolean; link: string | null; hadAccount: boolean }
  }
  'webAccess:revoke': { req: { authUserId: string }; res: void }
  'webAccess:setLocation': { req: { authUserId: string; locationId: string | null }; res: void }
  'webAccess:sessions': { req: void; res: WebSessionRow[] }
  'webAccess:endSession': { req: { sessionId: string }; res: void }

  'storeProfile:get': { req: void; res: StoreProfile }
  'storeProfile:update': { req: Partial<StoreProfile>; res: StoreProfile }

  // Online-store upsell. One read for both nudge surfaces, and one write for the
  // progress flags they act on — every caller wants the recomputed status back.
  'onlineStore:status': { req: void; res: OnlineStoreStatus }
  'onlineStore:markProgress': {
    req: { mode: 'snooze' | 'resume' | 'completedSetup' }
    res: OnlineStoreStatus
  }

  'media:upload': {
    req: { kind: 'product' | 'logo' | 'header'; contentType: string; bytes: Uint8Array }
    res: { key: string; publicUrl: string }
  }
}

export type IpcChannel = keyof IpcContract

export interface IpcErrorShape {
  code: string
  message: string
}

export type IpcEnvelope<T> = { ok: true; data: T } | { ok: false; error: IpcErrorShape }
